/**
 * Tokens de color SEMÁNTICOS del Design System — FUENTE DE VERDAD.
 *
 * Cada token semántico (Background/*, Text/*, Icon/*, Stroke/*) NO define un color propio: apunta a
 * un color de la paleta primitiva (`dsColors`). Así, si cambia un primitivo, el
 * semántico se actualiza solo. Traslado del frame "Semantic Colors" de Figma
 * (node 23:555).
 *
 * Uso en la app (import-only, igual que la paleta primitiva — ver ADR-675):
 *   import { semanticColors } from 'src/design-system/tokens/semantic-colors';
 *   <div style={{ color: semanticColors.text.strong }} />
 */
import { dsColors } from './colors';

type PaletteName = keyof typeof dsColors;

export interface SemanticColorToken {
  /** Nombre del token semántico, ej. 'Text/Strong'. */
  name: string;
  /** Propósito / cuándo usarlo. */
  purpose: string;
  /** Referencia al primitivo (display), ej. 'Color/Neutral/900'. */
  ref: string;
  /** Hex resuelto desde `dsColors`. */
  hex: string;
}

export interface SemanticColorGroup {
  name: string;
  tokens: SemanticColorToken[];
}

/** Resuelve una referencia a primitivo → { ref (display), hex } desde `dsColors`. */
function refColor(palette: PaletteName, step: string | number): { ref: string; hex: string } {
  const paletteLabel = palette.charAt(0).toUpperCase() + palette.slice(1);
  const stepStr = String(step);
  const stepLabel = /^\d/.test(stepStr)
    ? stepStr
    : stepStr.charAt(0).toUpperCase() + stepStr.slice(1);
  const hex = (dsColors[palette] as Record<string, string>)[stepStr];
  return { ref: `Color/${paletteLabel}/${stepLabel}`, hex };
}

/** Grupos para la página de docs (Semantic › Semantic Colors). */
export const SEMANTIC_COLOR_GROUPS: SemanticColorGroup[] = [
  {
    name: 'Background',
    tokens: [
      { name: 'Background/Base', purpose: 'Fondo principal', ...refColor('base', 'white') },
      {
        name: 'Background/Surface',
        purpose: 'El color de fondo para los elementos con mayor prominencia, como modales y cards.',
        ...refColor('neutral', 100),
      },
      {
        name: 'Background/Fill',
        purpose: 'Botones en el onboarding, sobre fondo oscuro',
        ...refColor('neutral', 50),
      },
      {
        name: 'Background/Fill weak',
        purpose: '-',
        ...refColor('neutral', 100),
      },
      {
        name: 'Background/Fill disabled',
        purpose: 'Botones deshabilitados',
        ...refColor('neutral', 400),
      },
      {
        name: 'Background/Fill inverse',
        purpose: 'Botones en el onboarding, sobre fondo claro',
        ...refColor('neutral', 900),
      },
      {
        name: 'Background/Fill brand',
        purpose: 'Relleno de acciones principales, como botones primarios y elementos interactivos destacados.',
        ...refColor('brand', 500),
      },
      {
        name: 'Background/Fill brand hover',
        purpose: 'Estado hover del relleno de marca en acciones principales.',
        ...refColor('brand', 600),
      },
      {
        name: 'Background/Fill error',
        purpose: 'Fondo de elementos que comunican un error o una acción destructiva.',
        ...refColor('red', 50),
      },
      {
        name: 'Background/Fill warning',
        purpose: 'Fondo de elementos que advierten precaución o una acción con riesgo.',
        ...refColor('yellow', 50),
      },
      {
        name: 'Background/Fill success',
        purpose: 'Fondo de elementos que confirman una acción exitosa.',
        ...refColor('green', 50),
      },
      {
        name: 'Background/Fill information',
        purpose: 'Fondo de elementos informativos o de mensajes neutrales.',
        ...refColor('blue', 50),
      },
    ],
  },
  {
    name: 'Text',
    tokens: [
      {
        name: 'Text/Strong',
        purpose:
          'Texto principal, como encabezados, contenido y etiquetas de formularios, para garantizar que sea destacado y legible.',
        ...refColor('neutral', 900),
      },
      {
        name: 'Text/Standard',
        purpose: 'Texto secundario, para reducir su prominencia visual.',
        ...refColor('neutral', 600),
      },
      {
        name: 'Text/Disabled',
        purpose: 'Utilizar para texto en estado deshabilitado.',
        ...refColor('neutral', 500),
      },
      {
        name: 'Text/Inverse strong',
        purpose: 'Usar en texto sobre fondo invertido.',
        ...refColor('neutral', 50),
      },
      {
        name: 'Text/Inverse weak',
        purpose: 'Utilizar para texto secundario sobre fondo invertido.',
        ...refColor('neutral', 300),
      },
      {
        name: 'Text/Link',
        purpose: 'Utilizar para enlaces de texto.',
        ...refColor('blue', 500),
      },
      {
        name: 'Text/Error',
        purpose: 'Utilizar para textos que comuniquen información crítica.',
        ...refColor('red', 500),
      },
      {
        name: 'Text/Warning',
        purpose: 'Utilizar para texto que comunique advertencias.',
        ...refColor('yellow', 500),
      },
      {
        name: 'Text/Success',
        purpose: 'Utilizar para textos que comuniquen éxito.',
        ...refColor('green', 500),
      },
      {
        name: 'Text/Information',
        purpose: 'Utilizar para textos que comuniquen información importante.',
        ...refColor('blue', 500),
      },
    ],
  },
  {
    name: 'Icon',
    tokens: [
      { name: 'Icon/Strong', purpose: '-', ...refColor('neutral', 900) },
      { name: 'Icon/Standard', purpose: '-', ...refColor('neutral', 600) },
      { name: 'Icon/Disabled', purpose: '-', ...refColor('neutral', 500) },
      { name: 'Icon/Inverse strong', purpose: '-', ...refColor('neutral', 50) },
      { name: 'Icon/Inverse weak', purpose: '-', ...refColor('neutral', 300) },
      { name: 'Icon/Link', purpose: '-', ...refColor('blue', 500) },
      { name: 'Icon/Error', purpose: '-', ...refColor('red', 500) },
      { name: 'Icon/Warning', purpose: '-', ...refColor('yellow', 500) },
      { name: 'Icon/Success', purpose: '-', ...refColor('green', 500) },
      { name: 'Icon/Information', purpose: '-', ...refColor('blue', 500) },
    ],
  },
  {
    name: 'Stroke',
    tokens: [
      { name: 'Stroke/Strong', purpose: '-', ...refColor('neutral', 400) },
      { name: 'Stroke/Standard', purpose: '-', ...refColor('neutral', 100) },
      { name: 'Stroke/Weak', purpose: '-', ...refColor('neutral', 100) },
      { name: 'Stroke/Inverse', purpose: '-', ...refColor('neutral', 900) },
      { name: 'Stroke/Link', purpose: '-', ...refColor('blue', 500) },
      { name: 'Stroke/Error', purpose: '-', ...refColor('red', 50) },
      { name: 'Stroke/Warning', purpose: '-', ...refColor('yellow', 50) },
      { name: 'Stroke/Success', purpose: '-', ...refColor('green', 50) },
      { name: 'Stroke/Information', purpose: '-', ...refColor('blue', 50) },
    ],
  },
];

/**
 * Mapa plano de tokens semánticos → hex (resueltos desde `dsColors`), para usar en
 * código de la app. Los valores son referencias a la paleta primitiva.
 */
export const semanticColors = {
  background: {
    base: dsColors.base.white,
    surface: dsColors.neutral[100],
    fill: dsColors.neutral[50],
    fillWeak: dsColors.neutral[100],
    fillDisabled: dsColors.neutral[400],
    fillInverse: dsColors.neutral[900],
    fillBrand: dsColors.brand[500],
    fillBrandHover: dsColors.brand[600],
    fillError: dsColors.red[50],
    fillWarning: dsColors.yellow[50],
    fillSuccess: dsColors.green[50],
    fillInformation: dsColors.blue[50],
  },
  text: {
    strong: dsColors.neutral[900],
    standard: dsColors.neutral[600],
    disabled: dsColors.neutral[500],
    inverseStrong: dsColors.neutral[50],
    inverseWeak: dsColors.neutral[300],
    link: dsColors.blue[500],
    error: dsColors.red[500],
    warning: dsColors.yellow[500],
    success: dsColors.green[500],
    information: dsColors.blue[500],
  },
  icon: {
    strong: dsColors.neutral[900],
    standard: dsColors.neutral[600],
    disabled: dsColors.neutral[500],
    inverseStrong: dsColors.neutral[50],
    inverseWeak: dsColors.neutral[300],
    link: dsColors.blue[500],
    error: dsColors.red[500],
    warning: dsColors.yellow[500],
    success: dsColors.green[500],
    information: dsColors.blue[500],
  },
  stroke: {
    strong: dsColors.neutral[400],
    standard: dsColors.neutral[100],
    weak: dsColors.neutral[100],
    inverse: dsColors.neutral[900],
    link: dsColors.blue[500],
    error: dsColors.red[50],
    warning: dsColors.yellow[50],
    success: dsColors.green[50],
    information: dsColors.blue[50],
  },
} as const;
